import React, { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Compass,
  GraduationCap,
  BookOpen,
  PanelLeftClose,
  PanelLeft,
  Clock,
  Sun,
  Moon,
  Coffee,
  ChevronDown,
  ChevronUp,
  User,
  Check,
  Trash2,
  Network,
} from 'lucide-react';
import { useData } from '../context/DataContext';
import { translations } from '../lib/i18n';
import {
  getRecentSessions,
  deleteRecentSession,
  clearAllRecentSessions,
  getSavedYear,
  saveSelectedYear,
  getSavedWilaya,
  saveSelectedWilaya,
} from '../lib/storage';
import type { Language, RecentSession, Department } from '../lib/types';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  isMobile: boolean;
  onCloseMobile?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onToggle,
  isMobile,
  onCloseMobile,
}) => {
  const {
    language,
    setLanguage,
    theme,
    setTheme,
    wilayas,
    faculties,
    subjects,
    getSubjectCount,
    getYearsForFaculty,
  } = useData();

  const t = translations[language];
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Persistent Selected Year (remembers choice in state + localStorage, default from URL or localStorage or 'fourth')
  const [selectedYear, setSelectedYear] = useState<Department>(() => {
    const fromUrl = searchParams.get('y') as Department | null;
    if (fromUrl) return fromUrl;
    const fromStorage = getSavedYear();
    if (fromStorage) return fromStorage;
    return 'fourth';
  });

  // Persistent Selected Wilaya
  const [selectedWilaya, setSelectedWilaya] = useState<string>(() => {
    const fromUrl = searchParams.get('w');
    if (fromUrl) return fromUrl;
    return getSavedWilaya();
  });

  // Sync state if URL changes
  useEffect(() => {
    const fromUrlY = searchParams.get('y') as Department | null;
    if (fromUrlY && fromUrlY !== selectedYear) {
      setSelectedYear(fromUrlY);
      saveSelectedYear(fromUrlY);
    }
    const fromUrlW = searchParams.get('w');
    if (fromUrlW && fromUrlW !== selectedWilaya) {
      setSelectedWilaya(fromUrlW);
      saveSelectedWilaya(fromUrlW);
    }
  }, [searchParams, selectedYear, selectedWilaya]);

  const activeFacultySlug = faculties[0]?.slug ?? '';

  const [recentSessions, setRecentSessions] = useState<RecentSession[]>([]);

  // Expandable sections inside the sidebar
  const [expandedSection, setExpandedSection] = useState<'wilayas' | 'years' | 'subjects' | null>(null);

  useEffect(() => {
    setRecentSessions(getRecentSessions());
  }, [location.pathname]);

  const toggleSection = (section: 'wilayas' | 'years' | 'subjects') => {
    setExpandedSection((prev) => (prev === section ? null : section));
  };

  const handleStartNew = () => {
    if (isMobile && onCloseMobile) onCloseMobile();
    navigate('/');
  };

  const handleOpenRecent = (session: RecentSession) => {
    if (isMobile && onCloseMobile) onCloseMobile();
    navigate(`/quiz/${session.lessonId}?w=${encodeURIComponent(session.wilaya)}`);
  };

  // Delete a specific session
  const handleDeleteSession = (e: React.MouseEvent, lessonId: string, wilaya: string) => {
    e.stopPropagation();
    const updated = deleteRecentSession(lessonId, wilaya);
    setRecentSessions(updated);
  };

  // Clear all sessions
  const handleClearAllSessions = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearAllRecentSessions();
    setRecentSessions([]);
  };

  // Nav 1: Wilayas
  const handleNavWilayas = () => {
    toggleSection('wilayas');
    if (location.pathname !== '/') {
      navigate(`/?w=${encodeURIComponent(selectedWilaya)}&f=${encodeURIComponent(activeFacultySlug)}`);
    }
  };

  const handleSelectWilayaFromSidebar = (wilayaState: string) => {
    setSelectedWilaya(wilayaState);
    saveSelectedWilaya(wilayaState);
    if (isMobile && onCloseMobile) onCloseMobile();
    navigate(
      `/?w=${encodeURIComponent(wilayaState)}&f=${encodeURIComponent(activeFacultySlug)}&y=${encodeURIComponent(
        selectedYear
      )}`
    );
  };

  // Nav 2: Years / Levels
  const handleNavLevels = () => {
    toggleSection('years');
    navigate(
      `/?w=${encodeURIComponent(selectedWilaya)}&f=${encodeURIComponent(activeFacultySlug)}&y=${encodeURIComponent(
        selectedYear
      )}`
    );
  };

  const handleSelectYearFromSidebar = (yearDept: Department) => {
    setSelectedYear(yearDept);
    saveSelectedYear(yearDept);
    if (isMobile && onCloseMobile) onCloseMobile();
    navigate(
      `/?w=${encodeURIComponent(selectedWilaya)}&f=${encodeURIComponent(activeFacultySlug)}&y=${encodeURIComponent(
        yearDept
      )}`
    );
  };

  // Nav 3: Modules / Subjects (filters by the chosen year, never reverting!)
  const handleNavModules = () => {
    toggleSection('subjects');
    navigate(
      `/?w=${encodeURIComponent(selectedWilaya)}&f=${encodeURIComponent(activeFacultySlug)}&y=${encodeURIComponent(
        selectedYear
      )}`
    );
  };

  const handleSelectSubjectFromSidebar = (subjectId: string, dept: Department) => {
    setSelectedYear(dept);
    saveSelectedYear(dept);
    if (isMobile && onCloseMobile) onCloseMobile();
    navigate(
      `/?w=${encodeURIComponent(selectedWilaya)}&f=${encodeURIComponent(
        activeFacultySlug
      )}&y=${encodeURIComponent(dept)}&s=${encodeURIComponent(subjectId)}`
    );
  };

  const activeFaculty = faculties[0] || null;
  const facultyYears = activeFaculty ? getYearsForFaculty(activeFaculty) : [];

  // Filtered subjects strictly matching the selectedYear
  const currentSubjects = useMemo(() => {
    const filtered = subjects.filter((s) => s.department === selectedYear);
    if (filtered.length > 0) return filtered;
    return subjects.slice(0, 15);
  }, [subjects, selectedYear]);

  const sidebarContent = (
    <div className="sidebar-inner-container">
      {/* Top Header: Logo + Collapse Button */}
      <div className="sidebar-top-header">
        <div className="sidebar-brand-link" onClick={() => navigate('/')}>
          <div className="sidebar-brand-icon">
            <GraduationCap size={18} />
          </div>
          <span className="sidebar-brand-name">{t.appName}</span>
        </div>

        <button
          type="button"
          className="sidebar-collapse-btn"
          onClick={onToggle}
          title={isOpen ? t.collapseSidebar : t.expandSidebar}
          aria-label={isOpen ? t.collapseSidebar : t.expandSidebar}
        >
          {isOpen ? <PanelLeftClose size={18} /> : <PanelLeft size={18} />}
        </button>
      </div>

      {/* Primary Action Button: + New Practice */}
      <div className="sidebar-action-wrap">
        <button
          type="button"
          className="sidebar-new-btn"
          onClick={handleStartNew}
        >
          <Plus size={16} />
          <span>{t.newQuiz}</span>
        </button>
      </div>

      {/* Main Navigation Items */}
      <nav className="sidebar-nav-menu" aria-label="Main Navigation">
        {/* 1. Wilayas Button */}
        <div>
          <button
            type="button"
            className={`sidebar-nav-item ${expandedSection === 'wilayas' ? 'active-item' : ''}`}
            onClick={handleNavWilayas}
          >
            <Compass size={17} />
            <span className="sidebar-nav-text">{t.navWilayas}</span>
            <span className="sidebar-nav-chevron">
              {expandedSection === 'wilayas' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          </button>

          {expandedSection === 'wilayas' && (
            <div className="sidebar-sub-accordion">
              {wilayas.slice(0, 12).map((w) => {
                const isSelected = selectedWilaya === w.state;
                const label = w.state === 'all' ? t.allWilayas : w.state;
                return (
                  <button
                    key={w.state}
                    type="button"
                    className={`sidebar-sub-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectWilayaFromSidebar(w.state)}
                  >
                    <span className="sub-item-name">{label}</span>
                    <span className="sub-item-count">{w.count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 2. Years / Levels Button (Remembers chosen year in memory and URL) */}
        <div>
          <button
            type="button"
            className={`sidebar-nav-item ${expandedSection === 'years' ? 'active-item' : ''}`}
            onClick={handleNavLevels}
          >
            <GraduationCap size={17} />
            <span className="sidebar-nav-text">
              {t.navLevels}
              <span className="sidebar-nav-subtag">({t.years[selectedYear]})</span>
            </span>
            <span className="sidebar-nav-chevron">
              {expandedSection === 'years' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          </button>

          {expandedSection === 'years' && (
            <div className="sidebar-sub-accordion">
              {facultyYears.map((dept) => {
                const isSelected = selectedYear === dept;
                const yearLabel = t.years[dept];
                return (
                  <button
                    key={dept}
                    type="button"
                    className={`sidebar-sub-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectYearFromSidebar(dept)}
                  >
                    <span className="sub-item-name">{yearLabel}</span>
                    {isSelected && <Check size={14} className="sub-item-check" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 3. Modules / Subjects Button (Always uses selectedYear, never resetting!) */}
        <div>
          <button
            type="button"
            className={`sidebar-nav-item ${expandedSection === 'subjects' ? 'active-item' : ''}`}
            onClick={handleNavModules}
          >
            <BookOpen size={17} />
            <span className="sidebar-nav-text">
              {t.navModules}
              <span className="sidebar-nav-subtag">({t.years[selectedYear]})</span>
            </span>
            <span className="sidebar-nav-chevron">
              {expandedSection === 'subjects' ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </span>
          </button>

          {expandedSection === 'subjects' && (
            <div className="sidebar-sub-accordion">
              {currentSubjects.map((sub) => {
                const isSelected = searchParams.get('s') === sub.id;
                const count = getSubjectCount(sub.id, selectedWilaya);
                return (
                  <button
                    key={sub.id}
                    type="button"
                    className={`sidebar-sub-item ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectSubjectFromSidebar(sub.id, sub.department)}
                    title={sub.name}
                  >
                    <span className="sub-item-name" dir="auto">{sub.name}</span>
                    <span className="sub-item-count">{count}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* 4. Mindmap Maker */}
        <div>
          <button
            type="button"
            className={`sidebar-nav-item ${location.pathname === '/mindmap' ? 'active-item' : ''}`}
            onClick={() => {
              if (isMobile && onCloseMobile) onCloseMobile();
              navigate('/mindmap');
            }}
          >
            <Network size={17} />
            <span className="sidebar-nav-text">{t.mindmapMaker}</span>
          </button>
        </div>
      </nav>

      {/* Recent Practice Sessions with DELETE button on each session */}
      <div className="sidebar-history-section">
        <div className="sidebar-section-header">
          <div className="sidebar-section-header-left">
            <Clock size={13} />
            <span>{t.recentPractices}</span>
          </div>
          {recentSessions.length > 0 && (
            <button
              type="button"
              className="sidebar-clear-all-btn"
              onClick={handleClearAllSessions}
              title={t.clearAllSessions}
            >
              {t.clearAllSessions}
            </button>
          )}
        </div>

        <div className="sidebar-recent-list">
          {recentSessions.length === 0 ? (
            <p className="sidebar-empty-recent">{t.noRecentPractices}</p>
          ) : (
            recentSessions.map((session, idx) => {
              const pct =
                session.totalQuestions > 0
                  ? Math.round((session.correctCount / session.totalQuestions) * 100)
                  : 0;

              return (
                <div
                  key={`${session.lessonId}_${session.wilaya}_${idx}`}
                  className="sidebar-recent-row"
                  onClick={() => handleOpenRecent(session)}
                  title={`${session.lessonName} (${session.wilaya})`}
                >
                  <span className="recent-bullet-dot" />
                  <span className="recent-item-title" dir="auto">
                    {session.lessonName}
                  </span>
                  {session.answeredCount > 0 && (
                    <span
                      className={`recent-item-badge ${
                        pct >= 70 ? 'good' : pct >= 50 ? 'medium' : 'low'
                      }`}
                    >
                      {pct}%
                    </span>
                  )}

                  {/* Single Session Delete Button */}
                  <button
                    type="button"
                    className="sidebar-delete-session-btn"
                    onClick={(e) => handleDeleteSession(e, session.lessonId, session.wilaya)}
                    title={t.deleteSession}
                    aria-label={t.deleteSession}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Bottom Profile & Controls */}
      <div className="sidebar-footer">
        <div className="sidebar-profile-card">
          <div className="user-avatar-circle">
            <User size={15} />
          </div>
          <div className="user-details">
            <span className="user-name">{t.appName}</span>
            <span className="user-role">{t.userRole}</span>
          </div>
        </div>

        {/* Compact Glass Pill for Lang & Theme */}
        <div className="sidebar-controls-bar">
          <div className="sidebar-lang-pills">
            {(['ar', 'fr', 'en'] as Language[]).map((lng) => (
              <button
                key={lng}
                type="button"
                className={`sidebar-lang-btn ${language === lng ? 'active' : ''}`}
                onClick={() => setLanguage(lng)}
              >
                {lng.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="sidebar-theme-toggles">
            <button
              type="button"
              className={`sidebar-theme-icon-btn ${theme === 'light' ? 'active' : ''}`}
              onClick={() => setTheme('light')}
              title={t.themeLight}
            >
              <Sun size={14} />
            </button>
            <button
              type="button"
              className={`sidebar-theme-icon-btn ${theme === 'cream' ? 'active' : ''}`}
              onClick={() => setTheme('cream')}
              title={t.themeCream}
            >
              <Coffee size={14} />
            </button>
            <button
              type="button"
              className={`sidebar-theme-icon-btn ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => setTheme('dark')}
              title={t.themeDark}
            >
              <Moon size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (isMobile) {
    if (!isOpen) return null;
    return (
      <div
        className="sidebar-mobile-backdrop"
        onClick={onCloseMobile}
        role="dialog"
        aria-modal="true"
      >
        <aside
          className="dashboard-sidebar mobile-drawer open"
          onClick={(e) => e.stopPropagation()}
        >
          {sidebarContent}
        </aside>
      </div>
    );
  }

  return (
    <aside className={`dashboard-sidebar ${isOpen ? 'open' : 'collapsed'}`}>
      {isOpen ? (
        sidebarContent
      ) : (
        <div className="sidebar-collapsed-strip">
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={onToggle}
            title={t.expandSidebar}
          >
            <PanelLeft size={18} />
          </button>
          <button
            type="button"
            className="sidebar-collapsed-action"
            onClick={handleStartNew}
            title={t.newQuiz}
          >
            <Plus size={18} />
          </button>
          <button
            type="button"
            className={`sidebar-collapsed-action ${location.pathname === '/mindmap' ? 'active' : ''}`}
            onClick={() => navigate('/mindmap')}
            title={t.mindmapMaker}
          >
            <Network size={17} />
          </button>
          <div className="sidebar-collapsed-avatar" title={t.userRole}>
            <User size={15} />
          </div>
        </div>
      )}
    </aside>
  );
};
